import React from 'react'
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { AirlineLogo } from '@/src/utils/confirmation/airlineUtils'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface HolidayTableColumn {
    key: string
    label: string
    align: 'left' | 'center' | 'right'
}

interface HolidayTableProps {
    /** Set only when the table is too dense to wrap legibly and must scroll instead. */
    minWidth?: number
    columns: HolidayTableColumn[]
    /** Cells pre-resolved against the columns — no missing-key branching in the view. */
    rows: Array<{
        id: string
        cells: Array<{
            key: string
            value: string
            align: 'left' | 'center' | 'right'
            /** Set when the cell is a flight number, so its carrier's logo can sit beside it. */
            carrierCode?: string
        }>
    }>
    caption?: string
    isMobile: boolean
}

const HolidayTable: React.FC<HolidayTableProps> = ({ minWidth, columns, rows, caption, isMobile }) => {
    return (
        <Stack spacing={1}>
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'borders.default',
                    borderRadius: HOLIDAYS_RADIUS.NESTED,
                    overflowX: 'auto'
                }}
            >
                {/*
                 * Headers wrap rather than run off the side. A six-column pricing table with
                 * `nowrap` headers forced the whole table into horizontal scroll beside the
                 * sidebar; letting the labels wrap keeps every column on screen.
                 */}
                <Table size={isMobile ? 'small' : 'medium'} sx={{ minWidth: minWidth ?? 'auto', tableLayout: 'auto' }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'neutral.100' }}>
                            {columns.map(column => (
                                <TableCell
                                    key={column.key}
                                    align={column.align}
                                    sx={{
                                        fontSize: isMobile ? '0.75rem' : '1rem',
                                        fontWeight: 700,
                                        lineHeight: 1.4,
                                        color: 'textColors.primary',
                                        borderBottom: '1px solid',
                                        borderColor: 'borders.default',
                                        verticalAlign: 'top',
                                        py: isMobile ? 1.25 : 2
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => (
                            <TableRow key={row.id} sx={{ '&:last-of-type td': { borderBottom: 'none' } }}>
                                {row.cells.map(cell => (
                                    <TableCell
                                        key={cell.key}
                                        align={cell.align}
                                        sx={{
                                            fontSize: isMobile ? '1rem' : '0.938rem',
                                            color: 'textColors.subtitle',
                                            borderBottom: '1px solid',
                                            borderColor: 'borders.default',
                                            py: isMobile ? 1.25 : 2,
                                            // Cells wrap. Some tables carry whole sentences (the
                                            // payment schedule), and `nowrap` pushed those off the
                                            // side instead of letting them run to a second line.
                                            whiteSpace: 'normal',
                                            lineHeight: 1.5
                                        }}
                                    >
                                        {cell.carrierCode ? (
                                            <Stack
                                                direction="row"
                                                spacing={0.75}
                                                sx={{
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        cell.align === 'center' ? 'center' : 'flex-start'
                                                }}
                                            >
                                                <AirlineLogo
                                                    airlineCode={cell.carrierCode}
                                                    width={isMobile ? 18 : 22}
                                                    height={isMobile ? 18 : 22}
                                                />
                                                <Box component="span">{cell.value}</Box>
                                            </Stack>
                                        ) : (
                                            cell.value
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>

            {caption && <Typography sx={{ fontSize: '0.75rem', color: 'textColors.subheading' }}>{caption}</Typography>}
        </Stack>
    )
}

export { HolidayTable }
export type { HolidayTableProps, HolidayTableColumn }
export default HolidayTable
